/**
 * Authentication Provider
 *
 * Delegates everything to the corporate SSO session established during
 * boot (src/main.tsx). There is no login form: with no session the app
 * never mounts — the user is redirected to the portal instead.
 */

import { AuthProvider, UserIdentity } from 'react-admin';
import { isAuthenticated, ssoLogout, getSsoIdentity, getAppRoles } from '@/auth/sso';
import { resolvePrimaryRole } from '@/auth/authSync';
import { clearAuthData } from '@/utils/auth';

/**
 * Layout-debug harness (dev server only, stripped from production builds):
 * a static auth provider so the UI can render without a session.
 */
const mockAuthProvider: AuthProvider = {
  login: async () => undefined,
  logout: async () => undefined,
  checkAuth: async () => undefined,
  checkError: async () => undefined,
  getIdentity: async () => ({ id: 'mock', fullName: 'Mock User' }),
  getPermissions: async () => 'superuser',
};

const realAuthProvider: AuthProvider = {
  /**
   * Login is a no-op: the SSO boot sequence authenticates before React
   * mounts, so react-admin never needs to collect credentials.
   */
  login: async (): Promise<void> => {
    // handled by the SSO provider during boot
  },

  /**
   * End the session and land on the portal.
   */
  logout: async (): Promise<void> => {
    clearAuthData();
    sessionStorage.clear();
    ssoLogout();
  },

  /**
   * Check if user is authenticated
   */
  checkAuth: async (): Promise<void> => {
    if (!isAuthenticated()) {
      throw new Error('No active session');
    }
  },

  /**
   * Handle API errors (401, 403, etc.)
   */
  checkError: async (error: { status?: number }): Promise<void> => {
    if (error?.status === 401) {
      clearAuthData();
      sessionStorage.clear();
      return Promise.reject({ message: 'Session expired', logoutUser: true });
    }

    // 403 means "authenticated but not allowed" — keep the session
    return Promise.resolve();
  },

  /**
   * Get user identity for display
   */
  getIdentity: async (): Promise<UserIdentity> => {
    const identity = getSsoIdentity();

    if (!identity) {
      throw new Error('No user found');
    }

    return {
      id: identity.id,
      fullName: identity.fullName,
      avatar: undefined,
      email: identity.email,
    };
  },

  /**
   * Get user permissions: the primary Contacts role
   * (superuser | comercial | marketing)
   */
  getPermissions: async (): Promise<string> => {
    return resolvePrimaryRole(getAppRoles()) ?? '';
  },
};

const useMockAuth = import.meta.env.DEV && import.meta.env.VITE_MOCK === '1';

export const authProvider = useMockAuth ? mockAuthProvider : realAuthProvider;

export default authProvider;
